// KRC v4.19b — Create React App compatible Firebase module.
// Place this file at src/firebase.js.

import { deleteApp, getApp, getApps, initializeApp } from "firebase/app";
import {
  ReCaptchaEnterpriseProvider,
  initializeAppCheck,
} from "firebase/app-check";
import {
  EmailAuthProvider,
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateEmail,
  updatePassword,
  verifyBeforeUpdateEmail,
} from "firebase/auth";
import {
  addDoc,
  collection,
  connectFirestoreEmulator,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable,
} from "firebase/functions";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const missingConfig = Object.entries(firebaseConfig)
  .filter(([, value]) => !value)
  .map(([key]) => key);

if (missingConfig.length > 0) {
  throw new Error(
    `Missing Firebase environment values: ${missingConfig.join(", ")}`,
  );
}

const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);
const functionsRegion =
  process.env.REACT_APP_KRC_FUNCTIONS_REGION || "australia-southeast1";
const functions = getFunctions(app, functionsRegion);

if (typeof window !== "undefined") {
  // Avoid duplicate App Check / emulator initialisation during CRA hot reloads.
  const globalState = (window.__KRC_FIREBASE__ ||= {});
  const appCheckSiteKey =
    process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY || "";

  if (!globalState.appCheck && appCheckSiteKey) {
    if (
      process.env.NODE_ENV !== "production" &&
      process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG === "true"
    ) {
      // Firebase reads this global before App Check initialises.
      window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }

    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
    globalState.appCheck = true;
  }

  if (
    !globalState.emulators &&
    process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true"
  ) {
    connectFirestoreEmulator(
      db,
      process.env.REACT_APP_FIRESTORE_EMULATOR_HOST || "127.0.0.1",
      Number(process.env.REACT_APP_FIRESTORE_EMULATOR_PORT || 8085),
    );
    connectAuthEmulator(
      auth,
      `http://${process.env.REACT_APP_AUTH_EMULATOR_HOST || "127.0.0.1"}:${
        process.env.REACT_APP_AUTH_EMULATOR_PORT || 9099
      }`,
      { disableWarnings: true },
    );
    connectFunctionsEmulator(
      functions,
      process.env.REACT_APP_FUNCTIONS_EMULATOR_HOST || "127.0.0.1",
      Number(process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || 5001),
    );
    globalState.emulators = true;
  }
}

// Creates the separate public self-signup Firebase identity without replacing
// the currently signed-in KRC operator session.
async function createAuthAccount(email, password) {
  const secondaryName = `krc-public-signup-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;
  const secondaryApp = initializeApp(firebaseConfig, secondaryName);
  const secondaryAuth = getAuth(secondaryApp);

  try {
    const credential = await createUserWithEmailAndPassword(
      secondaryAuth,
      email,
      password,
    );
    await signOut(secondaryAuth).catch(() => {});
    return credential.user.uid;
  } finally {
    await deleteApp(secondaryApp).catch(() => {});
  }
}

// Calls one of the trusted KRC Firebase callable Functions used by App.js.
async function callKrcFunction(name, payload = {}) {
  if (!name || typeof name !== "string") {
    throw new TypeError("A Firebase Function name is required.");
  }
  const callable = httpsCallable(functions, name);
  const response = await callable(payload);
  return response.data;
}

export {
  db,
  auth,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  collection,
  addDoc,
  getDocs,
  query,
  writeBatch,
  where,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  createAuthAccount,
  callKrcFunction,
  updateEmail,
  updatePassword,
  verifyBeforeUpdateEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
};
